<template>
  <div class="px-2 max-w-4xl mx-auto w-full overflow-hidden">
    <h3 class="text-xl mb-4 text-center font-['Formula1-Bold']">Big 2 Results</h3>

    <div class="relative shadow-md rounded-lg font-['Formula1-Regular'] w-full max-w-full bg-white border border-gray-200 overflow-hidden">

      <!-- Mobile-only sort control (table below is hidden on mobile) -->
      <div class="md:hidden flex items-center gap-2 p-3 border-b border-gray-200 bg-gray-50">
        <label class="text-xs text-gray-500 whitespace-nowrap">Sort by</label>
        <select :value="sortKey" @change="setSortKey($event.target.value)" class="text-xs border border-gray-300 rounded px-2 py-1 flex-1 bg-white">
          <option value="winner">Winner</option>
          <option value="second">Second</option>
          <option value="third">Third</option>
          <option value="loser">Loser</option>
          <option value="created_at">Datetime</option>
          <option value="location">Location</option>
        </select>
        <button @click="toggleSortDirection" class="text-xs px-2.5 py-1 border border-gray-300 rounded bg-white hover:bg-gray-100" :aria-label="sortAsc ? 'Ascending' : 'Descending'">
          {{ sortAsc ? '↑' : '↓' }}
        </button>
      </div>

      <!-- Mobile card layout -->
      <div class="md:hidden bg-gray-100 p-3 flex flex-col gap-2.5">
        <div v-if="paginatedData.length === 0" class="text-center text-gray-400 text-sm py-8">
          No results found
        </div>
        <div v-for="dataItem in paginatedData" :key="dataItem.id" class="bg-white rounded-lg border border-gray-200 border-l-4 border-l-[#FF0000] shadow-sm p-3 hover:shadow-md active:shadow-md transition-shadow">
          <div class="flex justify-between items-baseline mb-1.5 gap-2">
            <span class="font-bold text-gray-900 text-sm">{{ dataItem.winner }}</span>
            <span class="text-[11px] text-gray-400 whitespace-nowrap">{{ formatDateTime(dataItem.created_at) }}</span>
          </div>
          <div class="grid grid-cols-3 gap-2 text-xs text-gray-600 mb-1.5">
            <div><span class="text-gray-400 block text-[10px] uppercase tracking-wide">2nd</span>{{ dataItem.second }}</div>
            <div><span class="text-gray-400 block text-[10px] uppercase tracking-wide">3rd</span>{{ dataItem.third }}</div>
            <div><span class="text-gray-400 block text-[10px] uppercase tracking-wide">Last</span>{{ dataItem.loser }}</div>
          </div>
          <div v-if="dataItem.location" class="text-xs text-gray-500">{{ dataItem.location }}</div>
        </div>
      </div>

      <!-- Desktop / tablet table -->
      <div class="hidden md:block overflow-x-auto">
        <table class="w-full text-xs text-left rtl:text-right text-gray-800">

          <thead class="text-xs text-white uppercase bg-[#FF0000]">
            <tr>
              <th scope="col" class="px-3 py-3 cursor-pointer hover:bg-[#d40000] select-none whitespace-nowrap transition-colors" @click="sortBy('winner')">
                <div class="flex items-center gap-1">
                  Winner
                  <svg :class="['w-3 h-3 transition-all', sortKey === 'winner' ? 'opacity-100' : 'opacity-40', sortKey === 'winner' && !sortAsc ? 'rotate-180' : '']" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8.574 11.024h6.852a2.075 2.075 0 0 0 1.847-1.086 1.9 1.9 0 0 0-.11-1.986L13.736 2.9a2.122 2.122 0 0 0-3.472 0L6.837 7.952a1.9 1.9 0 0 0-.11 1.986 2.074 2.074 0 0 0 1.847 1.086Zm6.852 1.952H8.574a2.072 2.072 0 0 0-1.847 1.087 1.9 1.9 0 0 0 .11 1.985l3.426 5.05a2.123 2.123 0 0 0 3.472 0l3.427-5.05a1.9 1.9 0 0 0 .11-1.985 2.074 2.074 0 0 0-1.846-1.087Z"/>
                  </svg>
                </div>
              </th>
              <th scope="col" class="px-3 py-3 cursor-pointer hover:bg-[#d40000] select-none whitespace-nowrap transition-colors" @click="sortBy('second')">
                <div class="flex items-center gap-1">
                  Second
                  <svg :class="['w-3 h-3 transition-all', sortKey === 'second' ? 'opacity-100' : 'opacity-40', sortKey === 'second' && !sortAsc ? 'rotate-180' : '']" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8.574 11.024h6.852a2.075 2.075 0 0 0 1.847-1.086 1.9 1.9 0 0 0-.11-1.986L13.736 2.9a2.122 2.122 0 0 0-3.472 0L6.837 7.952a1.9 1.9 0 0 0-.11 1.986 2.074 2.074 0 0 0 1.847 1.086Zm6.852 1.952H8.574a2.072 2.072 0 0 0-1.847 1.087 1.9 1.9 0 0 0 .11 1.985l3.426 5.05a2.123 2.123 0 0 0 3.472 0l3.427-5.05a1.9 1.9 0 0 0 .11-1.985 2.074 2.074 0 0 0-1.846-1.087Z"/>
                  </svg>
                </div>
              </th>
              <th scope="col" class="px-3 py-3 cursor-pointer hover:bg-[#d40000] select-none whitespace-nowrap transition-colors" @click="sortBy('third')">
                <div class="flex items-center gap-1">
                  Third
                  <svg :class="['w-3 h-3 transition-all', sortKey === 'third' ? 'opacity-100' : 'opacity-40', sortKey === 'third' && !sortAsc ? 'rotate-180' : '']" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8.574 11.024h6.852a2.075 2.075 0 0 0 1.847-1.086 1.9 1.9 0 0 0-.11-1.986L13.736 2.9a2.122 2.122 0 0 0-3.472 0L6.837 7.952a1.9 1.9 0 0 0-.11 1.986 2.074 2.074 0 0 0 1.847 1.086Zm6.852 1.952H8.574a2.072 2.072 0 0 0-1.847 1.087 1.9 1.9 0 0 0 .11 1.985l3.426 5.05a2.123 2.123 0 0 0 3.472 0l3.427-5.05a1.9 1.9 0 0 0 .11-1.985 2.074 2.074 0 0 0-1.846-1.087Z"/>
                  </svg>
                </div>
              </th>
              <th scope="col" class="px-3 py-3 cursor-pointer hover:bg-[#d40000] select-none whitespace-nowrap transition-colors" @click="sortBy('loser')">
                <div class="flex items-center gap-1">
                  Loser
                  <svg :class="['w-3 h-3 transition-all', sortKey === 'loser' ? 'opacity-100' : 'opacity-40', sortKey === 'loser' && !sortAsc ? 'rotate-180' : '']" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8.574 11.024h6.852a2.075 2.075 0 0 0 1.847-1.086 1.9 1.9 0 0 0-.11-1.986L13.736 2.9a2.122 2.122 0 0 0-3.472 0L6.837 7.952a1.9 1.9 0 0 0-.11 1.986 2.074 2.074 0 0 0 1.847 1.086Zm6.852 1.952H8.574a2.072 2.072 0 0 0-1.847 1.087 1.9 1.9 0 0 0 .11 1.985l3.426 5.05a2.123 2.123 0 0 0 3.472 0l3.427-5.05a1.9 1.9 0 0 0 .11-1.985 2.074 2.074 0 0 0-1.846-1.087Z"/>
                  </svg>
                </div>
              </th>
              <th scope="col" class="px-3 py-3 cursor-pointer hover:bg-[#d40000] select-none whitespace-nowrap transition-colors" @click="sortBy('created_at')">
                <div class="flex items-center gap-1">
                  Datetime
                  <svg :class="['w-3 h-3 transition-all', sortKey === 'created_at' ? 'opacity-100' : 'opacity-40', sortKey === 'created_at' && !sortAsc ? 'rotate-180' : '']" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8.574 11.024h6.852a2.075 2.075 0 0 0 1.847-1.086 1.9 1.9 0 0 0-.11-1.986L13.736 2.9a2.122 2.122 0 0 0-3.472 0L6.837 7.952a1.9 1.9 0 0 0-.11 1.986 2.074 2.074 0 0 0 1.847 1.086Zm6.852 1.952H8.574a2.072 2.072 0 0 0-1.847 1.087 1.9 1.9 0 0 0 .11 1.985l3.426 5.05a2.123 2.123 0 0 0 3.472 0l3.427-5.05a1.9 1.9 0 0 0 .11-1.985 2.074 2.074 0 0 0-1.846-1.087Z"/>
                  </svg>
                </div>
              </th>
              <th scope="col" class="px-3 py-3 cursor-pointer hover:bg-[#d40000] select-none whitespace-nowrap max-w-[150px] transition-colors" @click="sortBy('location')">
                <div class="flex items-center gap-1">
                  Location
                  <svg :class="['w-3 h-3 transition-all', sortKey === 'location' ? 'opacity-100' : 'opacity-40', sortKey === 'location' && !sortAsc ? 'rotate-180' : '']" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8.574 11.024h6.852a2.075 2.075 0 0 0 1.847-1.086 1.9 1.9 0 0 0-.11-1.986L13.736 2.9a2.122 2.122 0 0 0-3.472 0L6.837 7.952a1.9 1.9 0 0 0-.11 1.986 2.074 2.074 0 0 0 1.847 1.086Zm6.852 1.952H8.574a2.072 2.072 0 0 0-1.847 1.087 1.9 1.9 0 0 0 .11 1.985l3.426 5.05a2.123 2.123 0 0 0 3.472 0l3.427-5.05a1.9 1.9 0 0 0 .11-1.985 2.074 2.074 0 0 0-1.846-1.087Z"/>
                  </svg>
                </div>
              </th>
            </tr>
          </thead>

          <tbody>
            <tr v-if="paginatedData.length === 0">
              <td colspan="6" class="text-center text-gray-400 py-6">No results found</td>
            </tr>
            <tr
              v-for="dataItem in paginatedData"
              :key="dataItem.id"
              class="odd:bg-white even:bg-gray-50 border-b border-gray-100 hover:bg-gray-100 text-gray-900 transition-colors"
            >
              <td class="px-3 py-2.5 font-medium">{{ dataItem.winner }}</td>
              <td class="px-3 py-2.5">{{ dataItem.second }}</td>
              <td class="px-3 py-2.5">{{ dataItem.third }}</td>
              <td class="px-3 py-2.5">{{ dataItem.loser }}</td>
              <td class="px-3 py-2.5 whitespace-nowrap">{{ formatDateTime(dataItem.created_at) }}</td>
              <td class="px-3 py-2.5 max-w-[300px] min-w-[200px] break-words">
                {{ dataItem.location }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <nav class="flex flex-col items-center justify-center p-4 border-t border-gray-100 bg-white gap-3 w-full" aria-label="Table navigation">
        <span class="text-xs font-normal text-gray-500 text-center">
          Showing <span class="font-semibold text-gray-900">{{ startIndex + 1 }}-{{ endIndex }}</span>
          of <span class="font-semibold text-gray-900">{{ totalItems }}</span>
        </span>

        <div class="w-full overflow-x-auto flex justify-center pb-1">
          <ul class="inline-flex -space-x-px text-xs h-8 list-none p-0 m-0">
            <li class="m-0 p-0">
              <button @click="prevPage" :disabled="currentPage === 1" class="flex items-center justify-center px-3 h-8 ms-0 leading-tight text-gray-500 bg-white border border-gray-300 rounded-s-lg hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">Prev</button>
            </li>

            <li v-for="page in totalPages" :key="page" class="m-0 p-0">
              <button @click="goToPage(page)" :class="['flex items-center justify-center px-3 h-8 leading-tight border border-gray-300 transition-colors', currentPage === page ? 'text-white bg-[#FF0000] border-[#FF0000] hover:bg-[#d40000] font-bold' : 'text-gray-500 bg-white hover:bg-gray-100 hover:text-gray-700']">{{ page }}</button>
            </li>

            <li class="m-0 p-0">
              <button @click="nextPage" :disabled="currentPage === totalPages" class="flex items-center justify-center px-3 h-8 leading-tight text-gray-500 bg-white border border-gray-300 rounded-e-lg hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">Next</button>
            </li>
          </ul>
        </div>
      </nav>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'ResultsPage',
  data() {
    return {
      apiData: [], 
      currentPage: 1,      
      itemsPerPage: 50,
      sortKey: 'created_at',
      sortAsc: false, 
    };
  },
  computed: {
    sortedData() {
      return [...this.apiData].sort((a, b) => {
        let modifier = this.sortAsc ? 1 : -1;
        let aVal = a[this.sortKey] || '';
        let bVal = b[this.sortKey] || '';
        
        if (this.sortKey === 'created_at') {
          aVal = new Date(aVal).getTime();
          bVal = new Date(bVal).getTime();
        } else {
          aVal = aVal.toString().toLowerCase();
          bVal = bVal.toString().toLowerCase();
        }

        if (aVal < bVal) return -1 * modifier;
        if (aVal > bVal) return 1 * modifier;
        return 0;
      });
    },
    totalItems() {
      return this.sortedData.length;
    },
    totalPages() {
      return Math.ceil(this.totalItems / this.itemsPerPage) || 1;
    },
    startIndex() {
      return (this.currentPage - 1) * this.itemsPerPage;
    },
    endIndex() {
      return Math.min(this.startIndex + this.itemsPerPage, this.totalItems);
    },
    paginatedData() {
      return this.sortedData.slice(this.startIndex, this.endIndex);
    }
  },
  methods: {
    formatDateTime(dateTimeString) {
      if (!dateTimeString) return '';
      const dateTime = new Date(dateTimeString);
      const date = dateTime.toLocaleDateString();
      const time = dateTime.toLocaleTimeString();
      return `${date} ${time}`;
    },
    sortBy(key) {
      if (this.sortKey === key) {
        this.sortAsc = !this.sortAsc;
      } else {
        this.sortKey = key;
        this.sortAsc = true;
      }
      this.currentPage = 1;
    },
    setSortKey(key) {
      this.sortKey = key;
      this.currentPage = 1;
    },
    toggleSortDirection() {
      this.sortAsc = !this.sortAsc;
      this.currentPage = 1;
    },
    nextPage() {
      if (this.currentPage < this.totalPages) this.currentPage++;
    },
    prevPage() {
      if (this.currentPage > 1) this.currentPage--;
    },
    goToPage(page) {
      this.currentPage = page;
    }
  },
  created() {
    document.title = 'Big 2 Results'; 
    axios.get('https://kbcardgames.xyz/api/big2-statistics/')
      .then(response => {
        this.apiData = response.data;
      })
      .catch(error => {
        console.error('Error:', error);
      });
  },
}
</script>

<style scoped>
@font-face {
  font-family: 'Formula1-Regular';
  src: url('@/assets/fonts/Formula1-Regular.otf') format('opentype'); 
}

@font-face {
  font-family: 'Formula1-Bold';
  src: url('@/assets/fonts/Formula1-Bold.otf') format('opentype'); 
}
</style>
